import { Injectable } from '@angular/core';
import {
  DatasourcePermissionAction,
  FunctionPermissionAction,
  GlobalVariablesPermissionAction,
  ModelsRegistryPermissionAction,
  PermissionService,
  PipelineFeatureEnableService,
  PipelineFeatureName,
  PipelinePermissionEntity,
  PresetsPermissionAction,
  SchedulerPermissionAction,
  WorkflowPermissionAction,
} from '@selfai-platform/shared';
import { MenuItem } from 'primeng/api';
import { BehaviorSubject, combineLatest, switchMap } from 'rxjs';
import {
  KE_DATASOURCES_PATH,
  KE_GLOBAL_VARIABLES_PATH,
  KE_MODELS_REGISTRY_PATH,
  KE_PRESETS_PATH,
  KE_ROOT_ROUTE,
  KE_SCHEDULES_PATH,
  KE_USER_FUNCTIONS_PATH,
} from '../navigation';

enum MenuItemKey {
  Workflows = 'workflows',
  Datasources = 'datasources',
  Presets = 'presets',
  Functions = 'functions',
  GlobalVariables = 'global-variables',
  Scheduling = 'scheduling',
  ModelsRegistry = 'models-registry',
}

@Injectable({ providedIn: 'root' })
export class PipelineMenuItemService {
  private pipelineMenuItems = new BehaviorSubject<MenuItem[]>([]);
  private menuItemsMap;

  public pipelineMenuItems$ = this.pipelineMenuItems.asObservable();

  constructor(
    private readonly permissionService: PermissionService,
    private readonly pipelineFeatureEnableService: PipelineFeatureEnableService,
  ) {
    this.menuItemsMap = new Map<MenuItemKey, MenuItem>([
      [
        MenuItemKey.Workflows,
        {
          label: 'shell.menu.workflows',
          icon: 'pi pi-fw pi-sitemap',
          routerLink: ['/', KE_ROOT_ROUTE],
        },
      ],
      [
        MenuItemKey.Datasources,
        {
          label: 'shell.menu.data-sources',
          icon: 'pi pi-fw pi-database',
          routerLink: ['/', KE_ROOT_ROUTE, KE_DATASOURCES_PATH],
        },
      ],
      [
        MenuItemKey.Presets,
        {
          label: 'shell.menu.presets',
          icon: 'pi pi-fw pi-microchip',
          routerLink: ['/', KE_ROOT_ROUTE, KE_PRESETS_PATH],
        },
      ],
      [
        MenuItemKey.Functions,
        {
          label: 'shell.menu.functions',
          icon: 'pi pi-fw pi-wave-pulse',
          routerLink: ['/', KE_ROOT_ROUTE, KE_USER_FUNCTIONS_PATH],
        },
      ],
      [
        MenuItemKey.GlobalVariables,
        {
          label: 'shell.menu.global-variables',
          icon: 'pi pi-fw pi-globe',
          routerLink: ['/', KE_ROOT_ROUTE, KE_GLOBAL_VARIABLES_PATH],
        },
      ],
      [
        MenuItemKey.Scheduling,
        {
          label: 'shell.menu.schedules',
          icon: 'pi pi-fw pi-calendar-clock',
          routerLink: ['/', KE_ROOT_ROUTE, KE_SCHEDULES_PATH],
        },
      ],
      [
        MenuItemKey.ModelsRegistry,
        {
          label: 'shell.menu.models-registry',
          icon: 'pi pi-fw pi-microchip-ai',
          routerLink: ['/', KE_ROOT_ROUTE, KE_MODELS_REGISTRY_PATH],
        },
      ],
    ]);

    this.permissionService.pipelinePermissionsLoadedObs$
      .pipe(
        switchMap(() =>
          combineLatest({
            [MenuItemKey.Workflows]: this.permissionService.checkPermission({
              entity: PipelinePermissionEntity.Workflows,
              action: WorkflowPermissionAction.List,
            }),
            [MenuItemKey.Datasources]: this.permissionService.checkPermission({
              entity: PipelinePermissionEntity.Datasources,
              action: DatasourcePermissionAction.List,
            }),
            [MenuItemKey.Presets]: this.permissionService.checkPermission({
              entity: PipelinePermissionEntity.Presets,
              action: PresetsPermissionAction.List,
            }),
            [MenuItemKey.Functions]: this.permissionService.checkPermission({
              entity: PipelinePermissionEntity.Functions,
              action: FunctionPermissionAction.List,
            }),
            [MenuItemKey.GlobalVariables]: this.permissionService.checkPermission({
              entity: PipelinePermissionEntity.GlobalVariables,
              action: GlobalVariablesPermissionAction.List,
            }),
            [MenuItemKey.Scheduling]: this.permissionService.checkPermission({
              entity: PipelinePermissionEntity.Schedules,
              action: SchedulerPermissionAction.List,
            }),
            [MenuItemKey.ModelsRegistry]: this.permissionService.checkPermission({
              entity: PipelinePermissionEntity.ModelsRegistry,
              action: ModelsRegistryPermissionAction.List,
            }),
          }),
        ),
      )
      .subscribe((permissions: Record<MenuItemKey, boolean>) => {
        this.setMenuItemsState(permissions);
      });
  }

  private setMenuItemsState(permissions: Record<MenuItemKey, boolean>) {
    const menuToFeatureMapping: Record<MenuItemKey, PipelineFeatureName> = {
      [MenuItemKey.Workflows]: PipelineFeatureName.WORKFLOWS,
      [MenuItemKey.Datasources]: PipelineFeatureName.DATASOURCES,
      [MenuItemKey.Presets]: PipelineFeatureName.PRESETS,
      [MenuItemKey.Functions]: PipelineFeatureName.FUNCTIONS,
      [MenuItemKey.Scheduling]: PipelineFeatureName.SCHEDULING,
      [MenuItemKey.ModelsRegistry]: PipelineFeatureName.ML_FLOW,
      [MenuItemKey.GlobalVariables]: PipelineFeatureName.GLOBAL_VARIABLES,
    };

    const availableItems = Object.entries(permissions)
      .filter(([, value]) => value)
      .filter(([key]) => {
        const featureName = menuToFeatureMapping[key as MenuItemKey];

        return featureName ? this.pipelineFeatureEnableService.isEnabledFeature(featureName) : true;
      })
      .map(([key]) => this.menuItemsMap.get(key as MenuItemKey));

    const menu: MenuItem[] = [];

    if (availableItems && availableItems.length > 0) {
      menu.push({
        id: 'engine',
        label: 'shell.menu.knowledge-engine',
        state: { sort: 300 },
        items: [...availableItems],
      });
    }

    this.pipelineMenuItems.next(menu);
  }
}
